import { SliderChangeEvent, SliderModule } from "primeng/slider";
import { Component, forwardRef, HostBinding, inject, input, Input, signal } from "@angular/core";
import { CustomInput } from "../../../../../classes/custom-input";
import { NG_VALUE_ACCESSOR, FormsModule, FormGroupDirective } from "@angular/forms";
import { FormErrorMessageComponent } from "@kernel/form/modules/tools/form-error-message/components/form-error-message/form-error-message.component";
import { isNumber } from "@kernel/shared/helpers/date-helper/number.helper";
import { compareDeep } from "@app/kernel/shared/helpers";

@Component({
  selector: "sxw-slider-input",
  templateUrl: "./slider-input.component.html",
  styleUrls: ["./slider-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderInputComponent),
      multi: true,
    },
  ],
  imports: [FormsModule, SliderModule, FormErrorMessageComponent],
})
export class SliderInputComponent extends CustomInput<number | number[]> {
  step = input(1);
  minValue = input(0);
  maxValue = input(10);
  range = input(false);
  sliderClass = input("");
  orientation = input<"horizontal" | "vertical">("horizontal");

  @HostBinding("class") @Input() theme = "";
  formGroupDirective = inject(FormGroupDirective, { optional: true });

  private cachedValue = signal<number | number[] | undefined>(undefined);

  private setValue(value: number | number[]) {
    if (typeof value === "number") {
      this.value = Math.min(this.maxValue(), Math.max(this.minValue(), value));
    } else {
      this.setArrayValue(value as number[]);
    }
    this.setCacheValue();
  }

  private setArrayValue(newValue: number[]) {
    const oldValue = this.cachedValue() as number[];
    if (oldValue?.length && newValue[0] > newValue[1]) {
      // if the first chunk of newValue[0] !== newValue[0] that means the change happened in first chunk only, and first chunk is greater than last one
      if (newValue[0] !== oldValue[0]) {
        this.value = [newValue[1], newValue[1]];
        return;
      }
      // if the second chunk of newValue[1] !== newValue[1] that means the change happened in second chunk only, and second chunk is less than first one
      if (oldValue.length && newValue[1] !== oldValue[1]) {
        this.value = [newValue[0], newValue[0]];
        return;
      }
    } else {
      this.value = newValue.map(v => {
        if (v < this.minValue()) return this.minValue();
        else if (v > this.maxValue()) return this.maxValue();
        return v;
      });
    }
  }

  private initValue() {
    this.value = this.range() ? [this.minValue(), this.maxValue()] : this.minValue();
    this.setCacheValue();
  }

  private setCacheValue() {
    this.cachedValue.set(this.range() ? [...(this.value as number[])] : (this.value as number));
  }

  override writeValue(value: number | number[]) {
    if ((value == null || value == undefined) && !this.value) {
      this.initValue();
    } else if (value && !this.value) this.value = Array.isArray(value) ? [...value] : value;
    else this.setValue(value);
    // use patchValue instead of onChange(), because onChange change the status of the form from outside like dirty
    // Note: check first if the values are different to prevent infinite loop, because patchValue will trigger writeValue again
    // TODO: find a better way to handle this
    if (!compareDeep(this.value, value)) {
      setTimeout(() => this.control?.patchValue(Array.isArray(this.value) ? [...this.value] : this.value));
    }
  }

  onSlideChange(event: SliderChangeEvent) {
    if (!event.values && !isNumber(event.value)) return;

    this.setValue(event.values?.length ? event.values : event.value!);
    this.onChange(Array.isArray(this.value) ? [...this.value] : this.value);
    this.onTouched();
  }
}
